import type { PaginationState, SortingState } from '@tanstack/react-table';
import { AlertTriangle, Info, Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '~/components/common/data-table/data-table';
import type { DataTableColumn } from '~/components/common/data-table/data-table.types';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { ProductCategory } from '~/features/catalog-operations/components/product-category';
import type {
  ProductColumn,
  SyncCapability,
} from '~/features/explorer/types/products-catalog.types';
import {
  PRODUCT_PAGE_SIZE_OPTIONS,
  getProductField,
  getProductImage,
  getProductPrice,
  getSourceStatusBadgeClass,
  isCapabilitySupported,
} from '~/features/explorer/utils/products-catalog';
import { cn } from '~/utils/cn';

import { ProductAvailabilityChip } from './product-availability-chip';
import type { ColumnVisibility } from './product-filter-bar';

type Product = Record<string, unknown>;

/** Backend sort keys for a header-sortable column (see PRODUCT_CATALOG_HEADER_SORT). */
export type HeaderSortMap = Record<string, { asc: string; desc: string }>;

interface ProductsTableProps {
  products: Product[];
  capabilities: SyncCapability[];
  apiColumns: ProductColumn[];
  isLoading: boolean;
  columnVisibility: ColumnVisibility;
  page: number;
  pageSize: number;
  totalRows: number;
  onPaginationChange: (pageIndex: number, pageSize: number) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onRowClick: (product: Product) => void;
  /** Opt-in: enable sortable column headers driving the server `sortBy`. When
   *  set, `sortBy`/`onSortByChange` are required. Off by default (integrations). */
  headerSortMap?: HeaderSortMap;
  /** Current server sort key (only read when `headerSortMap` is set). */
  sortBy?: string;
  /** Commit a new server sort key from a header click. */
  onSortByChange?: (sortBy: string) => void;
  /** Sort key to restore when a column is toggled back to unsorted. */
  defaultSortBy?: string;
  /** Opt-in: cap every cell at 320px / 3 lines and reveal full text on hover. */
  truncateCells?: boolean;
  /** Opt-in: pin the header and scroll the rows within this max-height. */
  maxHeight?: string | undefined;
  /** Opt-in: grow the table to fill the parent's height (flex-fill) instead of
   *  a fixed max-height. Parent must be a height-constrained flex column. */
  fillHeight?: boolean;
}

/** Maps the server `sortBy` key to TanStack's SortingState using the header map. */
function sortByToSorting(sortBy: string, map: HeaderSortMap): SortingState {
  for (const [columnId, keys] of Object.entries(map)) {
    if (sortBy === keys.asc) return [{ id: columnId, desc: false }];
    if (sortBy === keys.desc) return [{ id: columnId, desc: true }];
  }
  return [];
}

/** Maps a header click (SortingState) back to a server `sortBy` key; falls back
 *  to `fallback` when the column is cleared or isn't sortable. */
function sortingToSortBy(sorting: SortingState, map: HeaderSortMap, fallback: string): string {
  const first = sorting[0];
  if (!first) return fallback;
  const keys = map[first.id];
  if (!keys) return fallback;
  return first.desc ? keys.desc : keys.asc;
}

/** Clamps long cell text to 3 lines and shows the full value in a hover tooltip.
 *  Used only in truncate mode (Product Catalog). */
function TruncatedText({ value }: { value: string }) {
  if (!value || value === '—') {
    return <span className="text-muted-foreground/40 text-sm">—</span>;
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* No `block` here — `line-clamp-3` sets `display:-webkit-box`, and a
              `block` utility would override it and defeat the clamp. */}
          <span className="text-foreground line-clamp-3 text-sm break-words">{value}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[360px] break-words">
          {value}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Long free-text cell clamped to a few lines with a Show more / less toggle so
 *  verbose fields (e.g. description) don't blow up the row height. */
function ExpandableText({ value }: { value: string }) {
  const { t } = useTranslation('explorer');
  const [expanded, setExpanded] = useState(false);
  if (!value || value === '—') {
    return <span className="text-muted-foreground/40 text-sm">—</span>;
  }
  const isLong = value.length > 160;
  return (
    <div className="text-sm">
      {/* No `block` when clamping — `line-clamp` sets display:-webkit-box. */}
      <p className={cn('text-foreground break-words', !expanded && isLong && 'line-clamp-4')}>
        {value}
      </p>
      {isLong && (
        <Button
          type="button"
          variant="link"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="text-primary mt-0.5 h-auto cursor-pointer p-0 text-xs font-medium"
        >
          {expanded ? t('products.showLess') : t('products.showMore')}
        </Button>
      )}
    </div>
  );
}

/** Column codes rendered when the columns API is unavailable (loading/error). */
const FALLBACK_COLUMN_CODES = [
  'title',
  'brand',
  'source_status',
  'availability',
  'price_amount',
  'data_quality_score',
  'data_quality_grade',
  'critical_issues',
];

const GRADE_STYLES: Record<string, string> = {
  excellent: 'text-success bg-success-50 border-success/20',
  good: 'text-info bg-info-50 border-info/20',
  fair: 'text-warning bg-warning-50 border-warning/20',
  poor: 'text-error bg-error-50 border-error/20',
};

const NEUTRAL_BADGE = 'border-border bg-muted text-muted-foreground';

/** Per-column width hints. `title` keeps only a min-width so it absorbs any
 *  slack (preventing the large empty gaps from evenly-stretched columns);
 *  every other column is content-sized. Unknown/generic codes fall back to a
 *  sensible min-width. */
const COLUMN_CLASS: Record<string, string> = {
  title: 'min-w-[280px]',
  // Description is free text — give it a wide bound so it wraps into fewer
  // lines (shorter rows) than the default generic width.
  description: 'min-w-[360px] max-w-[640px]',
  brand: 'w-[150px]',
  source_status: 'w-[140px]',
  availability: 'w-[130px]',
  price_amount: 'w-[110px]',
  data_quality_score: 'w-[100px]',
  data_quality_grade: 'w-[120px]',
  critical_issues: 'w-[170px]',
};
const GENERIC_COLUMN_CLASS = 'min-w-[150px] max-w-[280px]';

/** Column width class. In truncate mode every column is capped at 320px so none
 *  can grow wider; otherwise the tuned per-column widths apply. */
function columnClassFor(code: string, truncateCells: boolean): string {
  if (!truncateCells) return COLUMN_CLASS[code] ?? GENERIC_COLUMN_CLASS;
  return code === 'title' ? 'min-w-[240px] max-w-[320px]' : 'min-w-[120px] max-w-[320px]';
}

/** Column codes never shown in the table — image links are redundant with the
 *  thumbnail already rendered in the Product column, and just add wide clutter. */
const HIDDEN_COLUMN_CODES = new Set([
  'image_link',
  'imageLink',
  'additional_image_links',
  'additionalImageLinks',
  'images',
]);

/** Converts a snake_case column code to its camelCase row-field alias. */
function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/** Safely coerces a loose (unknown) row value to a display string; objects and
 *  nullish values collapse to an empty string so no `[object Object]` leaks. */
function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

/**
 * Products data table (server-mode DataTable). Builds columns dynamically from
 * the connector column metadata (with a hardcoded fallback), applies specialized
 * cell renderers, honours external column visibility, and appends a gated
 * edit/delete Actions column.
 */
export function ProductsTable({
  products,
  capabilities,
  apiColumns,
  isLoading,
  columnVisibility,
  page,
  pageSize,
  totalRows,
  onPaginationChange,
  onEdit,
  onDelete,
  onRowClick,
  headerSortMap,
  sortBy,
  onSortByChange,
  defaultSortBy = 'score_desc',
  truncateCells = false,
  maxHeight,
  fillHeight = false,
}: ProductsTableProps) {
  const { t } = useTranslation('explorer');
  const canEdit = isCapabilitySupported(capabilities, 'update');
  const canDelete = isCapabilitySupported(capabilities, 'delete');
  const headerSortEnabled = !!headerSortMap;

  const columns = useMemo<DataTableColumn<Product>[]>(() => {
    const fallbackLabels: Record<string, string> = {
      title: t('products.colProduct'),
      brand: t('products.colVendor'),
      source_status: t('products.colStatus'),
      availability: t('products.colStock'),
      price_amount: t('products.price'),
      data_quality_score: t('products.score'),
      data_quality_grade: t('products.colQuality'),
      critical_issues: t('products.colIssues'),
    };

    const rawSources =
      apiColumns.length > 0
        ? apiColumns.map((c) => ({ code: c.code, label: c.label }))
        : FALLBACK_COLUMN_CODES.map((code) => ({ code, label: fallbackLabels[code] ?? code }));

    // Drop image-link columns, then pin `title` first and respect visibility.
    const sources = rawSources.filter((c) => !HIDDEN_COLUMN_CODES.has(c.code));
    const ordered = [
      ...sources.filter((c) => c.code === 'title'),
      ...sources.filter((c) => c.code !== 'title'),
    ].filter((c) => columnVisibility[c.code] !== false);

    // Google Product Category ships as a numeric GPC id — resolve it to the
    // readable name (dash when unresolvable), never the bare number.
    const categoryCell = (row: Product) => (
      <ProductCategory
        value={getProductField(row, 'googleProductCategory', 'google_product_category')}
      >
        {(display) =>
          truncateCells ? <TruncatedText value={display} /> : <ExpandableText value={display} />
        }
      </ProductCategory>
    );

    const renderers: Record<string, (row: Product) => ReactNode> = {
      google_product_category: categoryCell,
      googleProductCategory: categoryCell,
      title: (row) => {
        const label = getProductField(row, 'title', 'name', 'productName');
        const id = getProductField(row, 'offerId', 'offer_id', 'id', 'sku', 'externalId');
        const image = getProductImage(row);
        return (
          <div className="flex min-w-0 items-center gap-3">
            {image ? (
              <img
                src={image}
                alt=""
                className="bg-muted/30 h-11 w-11 shrink-0 rounded-lg border object-contain p-0.5"
                loading="lazy"
              />
            ) : (
              <div className="bg-muted/50 h-11 w-11 shrink-0 rounded-lg border" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm leading-snug font-medium">{label}</p>
              <p className="text-muted-foreground/60 mt-0.5 truncate font-mono text-[11px]">{id}</p>
            </div>
          </div>
        );
      },
      brand: (row) => (
        <span className="text-muted-foreground truncate text-sm">
          {getProductField(row, 'brand', 'vendor')}
        </span>
      ),
      source_status: (row) => {
        const s = (toStr(row.sourceStatus) || toStr(row.source_status)).toLowerCase();
        return (
          <Badge
            variant="outline"
            className={cn(
              'pointer-events-none w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize',
              getSourceStatusBadgeClass(s),
            )}
          >
            {s || '—'}
          </Badge>
        );
      },
      availability: (row) => (
        <ProductAvailabilityChip
          availability={toStr(row.availability)}
          emptyFallback={<span className="text-muted-foreground/40 text-sm">—</span>}
        />
      ),
      price_amount: (row) => (
        <span className="text-sm font-semibold tabular-nums">{getProductPrice(row)}</span>
      ),
      data_quality_score: (row) => {
        const score = row.dataQualityScore ?? row.data_quality_score;
        const grade = (toStr(row.dataQualityGrade) || toStr(row.data_quality_grade)).toLowerCase();
        if (score == null) return <span className="text-muted-foreground/40 text-sm">—</span>;
        return (
          <Badge
            variant="outline"
            className={cn(
              'pointer-events-none w-fit rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums',
              GRADE_STYLES[grade] ?? NEUTRAL_BADGE,
            )}
          >
            {Number(score).toFixed(0)}
          </Badge>
        );
      },
      data_quality_grade: (row) => {
        const grade = (toStr(row.dataQualityGrade) || toStr(row.data_quality_grade)).toLowerCase();
        if (!grade) return <span className="text-muted-foreground/40 text-sm">—</span>;
        return (
          <Badge
            variant="outline"
            className={cn(
              'pointer-events-none w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize',
              GRADE_STYLES[grade] ?? NEUTRAL_BADGE,
            )}
          >
            {grade}
          </Badge>
        );
      },
      critical_issues: (row) => {
        const critical = Number(row.criticalIssues ?? row.critical_issues ?? 0);
        const important = Number(row.importantIssues ?? row.important_issues ?? 0);
        const total = Number(row.totalIssues ?? row.total_issues ?? 0);
        if (total === 0) {
          return <span className="text-success text-xs font-medium">{t('products.noIssues')}</span>;
        }
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-foreground text-xs font-semibold tabular-nums">
              {t('products.issues', { count: total })}
            </span>
            <div className="flex items-center gap-2">
              {critical > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-error flex cursor-default items-center gap-1 text-[10px] font-medium">
                        <AlertTriangle className="h-2.5 w-2.5" />
                        {t('products.criticalCount', { count: critical })}
                        <Info className="h-2 w-2 opacity-50" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px] text-center">
                      {t('products.criticalTooltip', { count: critical })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {important > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-warning flex cursor-default items-center gap-1 text-[10px] font-medium">
                        {t('products.importantCount', { count: important })}
                        <Info className="h-2 w-2 opacity-50" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px] text-center">
                      {t('products.importantTooltip', { count: important })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        );
      },
    };

    const dataCols: DataTableColumn<Product>[] = ordered.map(({ code, label }) => {
      // A column is sortable via its header only when header-sort is on AND the
      // column has a backend sort key mapped.
      const sortingProp = headerSortEnabled ? { enableSorting: Boolean(headerSortMap[code]) } : {};
      const specialized = renderers[code];
      if (specialized) {
        return {
          id: code,
          header: label,
          cell: specialized,
          className: columnClassFor(code, truncateCells),
          ...sortingProp,
        };
      }
      // Generic renderer — reads the camelCase alias first, then the raw code.
      const camelKey = snakeToCamel(code);
      return {
        id: code,
        header: label,
        className: columnClassFor(code, truncateCells),
        ...sortingProp,
        cell: (row: Product) => {
          const raw = row[camelKey] ?? row[code];
          if (raw == null) return <span className="text-muted-foreground/40 text-sm">—</span>;
          const display = Array.isArray(raw) ? raw.map(toStr).join(', ') : toStr(raw);
          // Truncate mode: clamp to 3 lines + hover tooltip. Otherwise wrap the
          // full value within the column's max-width (no clamp).
          if (truncateCells) return <TruncatedText value={display} />;
          return <ExpandableText value={display} />;
        },
      };
    });

    if (!canEdit && !canDelete) return dataCols;

    const actionsCol: DataTableColumn<Product> = {
      id: '__actions__',
      header: '',
      className: 'w-[96px] text-right',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          {canEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(row);
                    }}
                    aria-label={t('products.editProduct')}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('products.edit')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {canDelete && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive h-7 w-7 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(row);
                    }}
                    aria-label={t('products.deleteProduct')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('products.delete')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    };

    return [...dataCols, actionsCol];
  }, [
    apiColumns,
    columnVisibility,
    canEdit,
    canDelete,
    onEdit,
    onDelete,
    t,
    truncateCells,
    headerSortEnabled,
    headerSortMap,
  ]);

  // Controlled header sorting (opt-in): translate the server `sortBy` to/from
  // TanStack's SortingState. Spread conditionally so the uncontrolled path is
  // untouched when header-sort is off (integrations).
  const sortingProps = headerSortEnabled
    ? {
        enableSorting: true,
        sorting: sortByToSorting(sortBy ?? '', headerSortMap),
        onSortingChange: (next: SortingState) => {
          onSortByChange?.(sortingToSortBy(next, headerSortMap, defaultSortBy));
        },
      }
    : {};

  return (
    <DataTable<Product>
      mode="server"
      columns={columns}
      data={products}
      rowKey={(row) =>
        toStr(row.id) || toStr(row.externalId) || toStr(row.offer_id) || toStr(row.offerId)
      }
      isLoading={isLoading}
      skeletonRows={8}
      totalRows={totalRows}
      pageSize={pageSize}
      pageSizeOptions={[...PRODUCT_PAGE_SIZE_OPTIONS]}
      pagination={{ pageIndex: page, pageSize }}
      onPaginationChange={(next: PaginationState) => {
        onPaginationChange(next.pageIndex, next.pageSize);
      }}
      onRowClick={onRowClick}
      emptyMessage={t('products.emptyTable')}
      tableItemType={t('products.rowLabel')}
      maxHeight={maxHeight}
      fillHeight={fillHeight}
      {...sortingProps}
    />
  );
}
